import * as Sentry from "@sentry/react";
import { ENVIRONMENT, SENTRY_DSN } from "./environmentVariables";

/**
 * Initialize Sentry for error tracking and performance monitoring
 * This should be imported as early as possible in the application lifecycle
 */
export const initSentry = (): void => {
  if (!SENTRY_DSN) {
    console.warn("Sentry DSN not found. Error tracking is disabled.");
    return;
  }
  const environment = ENVIRONMENT === "prod" ? "production" : ENVIRONMENT;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: environment,
    debug: environment === "local",
    // Setting this option to true will send default PII data to Sentry
    sendDefaultPii: false,
    // Enable tracing for performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
    // We recommend adjusting this value in production
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    // Only send errors in production unless explicitly enabled in development
    enabled:
      process.env.NODE_ENV === "production" ||
      process.env.REACT_APP_ENABLE_SENTRY_DEV === "true",
  });
};

/**
 * Set user information for Sentry tracking when a user logs in
 * @param user User data object containing id, email or other identifying information
 */
export const setSentryUser = (user: {
  id?: string;
  email?: string;
  [key: string]: any;
}): void => {
  Sentry.setUser(user);
};

/**
 * Clear user information when a user logs out
 */
export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};

/**
 * Manually capture an exception with Sentry
 * @param error Error object
 * @param context Additional context to include with the error
 */
export const captureException = (
  error: Error,
  context?: Record<string, any>
): void => {
  Sentry.captureException(error, { extra: context });
};

// Export Sentry directly for advanced use cases
export { Sentry };
